import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { signIn as apiSignIn, signUp as apiSignUp } from '@/api/auth.js';
import { setAuthToken, clearAuthToken } from '@/api/client.js';

const TOKEN_KEY = 'dg_access_token';
const USER_KEY  = 'dg_user';

function loadStored() {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const user  = JSON.parse(localStorage.getItem(USER_KEY) || 'null');
    // Prime the API client immediately so any effect that fires before
    // AuthProvider's own useEffect (e.g. child component fetches) already
    // has the token available.
    if (token) setAuthToken(token);
    return { token, user };
  } catch {
    return { token: null, user: null };
  }
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const stored = loadStored();
  const [token, setToken] = useState(stored.token);
  const [user,  setUser]  = useState(stored.user);

  // Keep the API client in sync on subsequent token changes (sign-in/sign-out).
  useEffect(() => {
    if (token) setAuthToken(token);
    else       clearAuthToken();
  }, [token]);

  const signIn = useCallback(async (email, password) => {
    const res = await apiSignIn(email, password);
    localStorage.setItem(TOKEN_KEY, res.access_token);
    localStorage.setItem(USER_KEY,  JSON.stringify(res.user));
    setToken(res.access_token);
    setUser(res.user);
    return res;
  }, []);

  const signUp = useCallback(async (email, password) => {
    const res = await apiSignUp(email, password);
    localStorage.setItem(TOKEN_KEY, res.access_token);
    localStorage.setItem(USER_KEY,  JSON.stringify(res.user));
    setToken(res.access_token);
    setUser(res.user);
    return res;
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    clearAuthToken();
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
